/**
 * A regra do funil, em código puro: sem banco, sem tela, sem relógio escondido.
 *
 * Cada função é o porte de um trecho de `portal/admin.html`, com a linha ao lado. Separar a regra
 * de quem abre conexão é o que permite provar cada caso de borda (papel financeiro arrastando,
 * voltar de "Pagamento a receber", indicador digitado com outra caixa) sem subir o Supabase.
 *
 * DOIS COMPORTAMENTOS DO LEGADO QUE PARECEM DEFEITO moram aqui isolados, cada um num nome só, para
 * a correção ser de uma linha se o fundador decidir corrigir. Até lá, a migração os reproduz:
 *   1. `TOQUE_ACEITA_SOLTAR_EM_PAGAMENTO_RECEBIDO`: o toque aceita soltar onde o mouse recusa.
 *   2. `idDoIndicadorExternoComoOLegadoGrava`: o `parseInt` sobre um id que é `uuid`.
 */

// Os rótulos e a ordem das etapas, que decidem "para trás" e os textos dos avisos.
import { JORNADA_ETAPA_LABEL, ORDEM_STATUS, ORIGENS_LEAD, STATUS_LEAD } from './constantes.js';
// A moeda do admin atual, "R$ 1.234,56", usada no rótulo do gift card e do cupom.
import { formatarMoeda, formatarTelefone } from '../../comum/formatos.js';

// ---------------------------------------------------------------------------------------------
// Os tipos que a regra enxerga (só os campos que ela lê)
// ---------------------------------------------------------------------------------------------

/** O papel da pessoa no painel, como `usuarios_sistema.papel` guarda. */
export type Papel = 'administrador' | 'consulta' | 'financeiro' | 'marketing' | string;

/**
 * O lead como o quadro o guarda em memória. MUTÁVEL de propósito: o legado altera o próprio
 * objeto (`lead.status = novoStatus`, `admin.html:7008`) e redesenha depois, e a tela nova faz o
 * mesmo para o quadro mudar nos MESMOS instantes em que mudava.
 */
export interface LeadDoQuadro {
  id: number;
  nome: string;
  contato_whatsapp: string | null;
  email: string | null;
  origem: string;
  status: string;
  observacoes: string | null;
  produtos: string[];
  indicado_por_externo_id: string | number | null;
  indicado_por_user_id: string | null;
  user_id_vinculado: string | null;
  data_criacao: string;
}

/** A cliente da lista, como `todosClientes` do legado (`admin.html:3600-3613`). */
export interface ClienteDaLista {
  readonly user_id: string;
  readonly email: string | null;
  readonly nome_completo: string | null;
}

/** A pessoa de fora que indicou. */
export interface IndicadorExterno {
  readonly id: string;
  readonly nome: string;
}

/** O gift card como o quadro o guarda. Mutável pelo mesmo motivo do lead (`admin.html:6574`). */
export interface GiftCardDoQuadro {
  id: number;
  codigo: string;
  valor: number;
  nome_destinatario: string | null;
  validade: string | null;
  resgatado_em: string | null;
  lead_id_resgate: number | null;
}

// ---------------------------------------------------------------------------------------------
// Máscara, WhatsApp e texto do card
// ---------------------------------------------------------------------------------------------

/**
 * A máscara do WhatsApp ao digitar (`admin.html:6497-6503`): até 11 dígitos, `(99`, `(99) 99999`,
 * `(99) 99999-9999`. Aplicada a cada tecla, sobre o valor inteiro, como o `input` do legado.
 */
export function aplicarMascaraWhatsapp(valor: string): string {
  // Só dígitos, e no máximo 11 (DDD mais celular), o resto é descartado.
  const digitos = valor.replace(/\D/g, '').slice(0, 11);
  // Nada digitado: campo vazio, e não "(".
  if (digitos.length === 0) return '';
  // Ainda no DDD: parêntese aberto.
  if (digitos.length <= 2) return `(${digitos}`;
  // DDD fechado, número sem hífen até o quinto dígito.
  if (digitos.length <= 7) return `(${digitos.slice(0, 2)}) ${digitos.slice(2)}`;
  // Número completo, com hífen depois do quinto dígito.
  return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
}

/**
 * O endereço do WhatsApp Web do telefone (`admin.html:7711-7713`): só dígitos, com 55 na frente
 * quando o número ainda não começa por ele.
 */
export function urlDoWhatsapp(telefone: string): string {
  const digitos = telefone.replace(/\D/g, '');
  const numero = digitos.startsWith('55') ? digitos : `55${digitos}`;
  return `https://web.whatsapp.com/send?phone=${numero}`;
}

/**
 * O nome de quem indicou o lead (`admin.html:7547-7556`): a cliente da lista, pelo nome ou pelo
 * e-mail, ou a pessoa de fora, pelo nome. Sem achar, `null`, e o card não mostra a linha.
 */
export function nomeIndicador(
  lead: Pick<LeadDoQuadro, 'indicado_por_user_id' | 'indicado_por_externo_id'>,
  clientes: readonly ClienteDaLista[],
  externos: readonly IndicadorExterno[],
): string | null {
  if (lead.indicado_por_user_id) {
    const cliente = clientes.find((c) => c.user_id === lead.indicado_por_user_id);
    return cliente?.nome_completo || cliente?.email || null;
  }
  if (lead.indicado_por_externo_id) {
    // Igualdade estrita, como o legado: id numérico gravado pelo `parseInt` não casa com o uuid.
    const externo = externos.find((i) => i.id === lead.indicado_por_externo_id);
    return externo?.nome || null;
  }
  return null;
}

/** As partes da linha de dados do card, na ordem do legado (`admin.html:6613-6622`). */
export interface MetaDoCard {
  // O telefone formatado, ou `null` sem telefone.
  readonly telefone: string | null;
  // O endereço do WhatsApp Web, junto do telefone.
  readonly whatsapp: string | null;
  // E-mail, origem, "Indicado por: X" e a data, só os que existem, na ordem do legado.
  readonly outros: readonly string[];
}

/**
 * Monta a linha de dados do card (`admin.html:6613-6622`). O e-mail cai para o da cliente vinculada
 * quando o lead não tem; a origem desconhecida aparece como veio; a data é `dd/mm/aaaa`.
 */
export function metaDoCard(
  lead: LeadDoQuadro,
  clientes: readonly ClienteDaLista[],
  externos: readonly IndicadorExterno[],
): MetaDoCard {
  // A data de criação no formato brasileiro, e vazia se não houver.
  const data = lead.data_criacao ? new Date(lead.data_criacao).toLocaleDateString('pt-BR') : '';
  // O e-mail do lead, ou o da cliente vinculada (a lista só existe para quem a carregou).
  const email =
    lead.email || (lead.user_id_vinculado ? clientes.find((c) => c.user_id === lead.user_id_vinculado)?.email : null);
  const indicador = nomeIndicador(lead, clientes, externos);
  // `filter(Boolean)` do legado: vazio, nulo e indefinido saem da linha.
  const outros = [email, ORIGENS_LEAD[lead.origem] || lead.origem, indicador ? `Indicado por: ${indicador}` : null, data].filter(
    (parte): parte is string => Boolean(parte),
  );
  return {
    telefone: lead.contato_whatsapp ? formatarTelefone(lead.contato_whatsapp) : null,
    whatsapp: lead.contato_whatsapp ? urlDoWhatsapp(lead.contato_whatsapp) : null,
    outros,
  };
}

// ---------------------------------------------------------------------------------------------
// Gift card e cupom
// ---------------------------------------------------------------------------------------------

/**
 * Vigente = não resgatado E (sem validade OU validade não vencida) (`admin.html:6539-6543`).
 * A validade vale até a meia-noite LOCAL do dia (`'T00:00:00'` sem fuso), como no legado.
 */
export function giftCardVigente(gc: GiftCardDoQuadro, agora: Date): boolean {
  if (gc.resgatado_em) return false;
  if (gc.validade && new Date(`${gc.validade}T00:00:00`) < agora) return false;
  return true;
}

/** O rótulo do gift card na lista: `CÓDIGO · R$ valor (pra destinatário)` (`admin.html:6556`). */
export function rotuloGiftCard(gc: GiftCardDoQuadro): string {
  // Destinatário nulo vira texto vazio, como o `escaparHtml` do legado (`admin.html:7638`).
  return `${gc.codigo} · ${formatarMoeda(Number(gc.valor))} (pra ${gc.nome_destinatario ?? ''})`;
}

/** Os gift cards já resgatados pelo lead, em texto: `CÓDIGO (R$ valor)`, separados por vírgula. */
export function textoGiftCardsDoLead(doLead: readonly GiftCardDoQuadro[]): string {
  return doLead.map((gc) => `${gc.codigo} (${formatarMoeda(Number(gc.valor))})`).join(', ');
}

/** O cupom e o resgate, só com o que a linha do card lê. */
export interface CupomDoQuadro {
  readonly id: number;
  readonly codigo: string;
  readonly tipo_desconto: string;
  readonly valor_desconto: number;
}
export interface ResgateDoQuadro {
  readonly cupom_id: number;
  readonly codigo: string | null;
  readonly lead_id: number | null;
  readonly aplicado_em: string | null;
  readonly valor_aplicado: number | null;
}

/**
 * Uma linha do cupom informado pela cliente (`admin.html:6720-6725`):
 * `CÓDIGO · 10% de desconto · aplicado, R$ X abatido` ou `· informado, aguardando pagamento`.
 */
export function textoDoCupom(resgate: ResgateDoQuadro, cupons: readonly CupomDoQuadro[]): string {
  const cupom = cupons.find((c) => c.id === resgate.cupom_id);
  // Sem o cupom carregado, o valor fica vazio, como no legado.
  const valor = cupom
    ? cupom.tipo_desconto === 'percentual'
      ? `${Number(cupom.valor_desconto)}%`
      : formatarMoeda(Number(cupom.valor_desconto))
    : '';
  const situacao = resgate.aplicado_em
    ? `aplicado, ${formatarMoeda(Number(resgate.valor_aplicado))} abatido`
    : 'informado, aguardando pagamento';
  return `${resgate.codigo || cupom?.codigo || '(?)'} · ${valor} de desconto · ${situacao}`;
}

// ---------------------------------------------------------------------------------------------
// Indicação
// ---------------------------------------------------------------------------------------------

/** Uma opção do "Indicado por": `cliente:<user_id>` ou `externo:<id>`. */
export interface OpcaoIndicador {
  readonly valor: string;
  readonly rotulo: string;
}

/**
 * Clientes e pessoas de fora numa lista só, em ordem alfabética `pt-BR` (`admin.html:7472-7482`).
 * O "(selecione)" e o "Nenhum dos acima" são da tela, antes e depois desta lista.
 */
export function construirOpcoesIndicadoPor(
  clientes: readonly ClienteDaLista[],
  externos: readonly IndicadorExterno[],
): OpcaoIndicador[] {
  const deClientes = clientes.map((c) => ({ valor: `cliente:${c.user_id}`, rotulo: c.nome_completo || c.email || '(sem nome)' }));
  const deExternos = externos.map((i) => ({ valor: `externo:${i.id}`, rotulo: i.nome }));
  return [...deClientes, ...deExternos].sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR'));
}

/** O valor do "Indicado por" que o card abre escolhido (`admin.html:7484-7488`). */
export function valorIndicadoPorDoLead(lead: Pick<LeadDoQuadro, 'indicado_por_user_id' | 'indicado_por_externo_id'>): string {
  if (lead.indicado_por_user_id) return `cliente:${lead.indicado_por_user_id}`;
  if (lead.indicado_por_externo_id) return `externo:${lead.indicado_por_externo_id}`;
  return '';
}

/** As duas colunas que o lead grava para dizer quem indicou. */
export interface ColunasDoIndicador {
  readonly indicado_por_user_id: string | null;
  readonly indicado_por_externo_id: string | number | null;
}

/**
 * COMPORTAMENTO DO LEGADO ISOLADO PARA DECISÃO DO FUNDADOR (achado da frente A4, 19/09/2026).
 *
 * O legado grava `parseInt(<id>, 10)` quando a pessoa escolhe uma pessoa de fora JÁ cadastrada
 * (`admin.html:7508`). Só que o id é `uuid` (`supabase/migrations/20260812_010_base.sql:25`), e
 * `parseInt` de um uuid devolve os dígitos do começo (um uuid que começa por "7b3..." vira 7, e o
 * banco recusa com "invalid input syntax for type uuid") ou `NaN` (um que começa por letra, que o
 * JSON manda como `null`, e o indicador some sem aviso).
 *
 * A migração reproduz, idêntico. Se o fundador mandar corrigir, a troca é esta linha: devolver o
 * `id` como veio.
 */
export function idDoIndicadorExternoComoOLegadoGrava(idDoSelect: string): number {
  return parseInt(idDoSelect, 10);
}

/** O que resolver o "Indicado por" pede: colunas prontas, ou cadastrar a pessoa antes. */
export type ResolucaoDoIndicador =
  | { readonly tipo: 'pronto'; readonly colunas: ColunasDoIndicador }
  | { readonly tipo: 'cadastrar'; readonly nome: string };

// Nenhum indicador: as duas colunas nulas.
const SEM_INDICADOR: ColunasDoIndicador = Object.freeze({ indicado_por_user_id: null, indicado_por_externo_id: null });

/**
 * A parte pura de `resolverIndicadoPor` (`admin.html:7493-7512`). "Nenhum dos acima" com nome
 * procura a pessoa sem diferenciar caixa e SÓ pede cadastro quando não acha; sem nome, nenhum
 * indicador. O cadastro em si é da tela, que tem a fonte de dados.
 */
export function resolverIndicador(
  valorSelect: string,
  nomeNovo: string | null | undefined,
  externos: readonly IndicadorExterno[],
): ResolucaoDoIndicador {
  if (valorSelect === 'novo') {
    const nome = (nomeNovo || '').trim();
    if (!nome) return { tipo: 'pronto', colunas: SEM_INDICADOR };
    // A mesma comparação do legado: o nome guardado aparado, os dois em minúsculas.
    const existente = externos.find((i) => i.nome.trim().toLowerCase() === nome.toLowerCase());
    if (existente) return { tipo: 'pronto', colunas: { indicado_por_user_id: null, indicado_por_externo_id: existente.id } };
    return { tipo: 'cadastrar', nome };
  }
  if (valorSelect && valorSelect.startsWith('cliente:')) {
    return { tipo: 'pronto', colunas: { indicado_por_user_id: valorSelect.replace('cliente:', ''), indicado_por_externo_id: null } };
  }
  if (valorSelect && valorSelect.startsWith('externo:')) {
    return {
      tipo: 'pronto',
      colunas: { indicado_por_user_id: null, indicado_por_externo_id: idDoIndicadorExternoComoOLegadoGrava(valorSelect.replace('externo:', '')) },
    };
  }
  return { tipo: 'pronto', colunas: SEM_INDICADOR };
}

/**
 * O ranking "Quem mais indica" (`admin.html:7559-7566`): conta por NOME de quem indicou, maior
 * primeiro. Empate mantém a ordem de chegada, que é a do objeto do legado.
 */
export function rankingDeIndicadores(
  indicacoes: readonly Pick<LeadDoQuadro, 'indicado_por_user_id' | 'indicado_por_externo_id'>[],
  clientes: readonly ClienteDaLista[],
  externos: readonly IndicadorExterno[],
): [string, number][] {
  // Objeto e não Map de propósito: é a mesma estrutura do legado, e com ela a mesma ordem de chaves.
  const contagem: Record<string, number> = {};
  for (const lead of indicacoes) {
    const nome = nomeIndicador(lead, clientes, externos);
    if (!nome) continue;
    contagem[nome] = (contagem[nome] || 0) + 1;
  }
  return Object.entries(contagem).sort((a, b) => b[1] - a[1]);
}

/** `N indicação` ou `N indicações` (`admin.html:7575`). */
export function textoIndicacoes(quantidade: number): string {
  return `${quantidade} indicaç${quantidade === 1 ? 'ão' : 'ões'}`;
}

// ---------------------------------------------------------------------------------------------
// Papel e movimento
// ---------------------------------------------------------------------------------------------

/** O que cada papel vê e faz no card (`admin.html:6600-6609`, `3367-3397`). */
export interface RecorteDoPapel {
  // Financeiro: só move para "Pagamento a receber"; não vê vínculo, produtos, gift card nem observações.
  readonly somenteFinanceiro: boolean;
  // Marketing: só edita origem e observações; não move, não vê o select de status.
  readonly somenteMarketing: boolean;
  // Os dois acima juntos: não veem vínculo, produtos, gift card, cupom, nem abrem a ficha.
  readonly somenteLeituraFunil: boolean;
  // Apenas consulta: tudo desabilitado, sem botão de ação e sem arrasto de mouse (`admin.html:3393-3397`).
  readonly consulta: boolean;
  // Excluir é só do administrador (`admin.html:3375`).
  readonly podeExcluir: boolean;
  // `draggable` do card: marketing nunca (`6609`), consulta desligado pelo observador (`3396`).
  readonly arrastaComMouse: boolean;
  // O toque barra SÓ marketing (`admin.html:6904`); consulta arrasta e o soltar não grava (`6978`).
  readonly arrastaComToque: boolean;
}

export function recorteDoPapel(papel: Papel): RecorteDoPapel {
  const somenteFinanceiro = papel === 'financeiro';
  const somenteMarketing = papel === 'marketing';
  const consulta = papel === 'consulta';
  return {
    somenteFinanceiro,
    somenteMarketing,
    somenteLeituraFunil: somenteFinanceiro || somenteMarketing,
    consulta,
    podeExcluir: papel === 'administrador',
    arrastaComMouse: !somenteMarketing && !consulta,
    arrastaComToque: !somenteMarketing,
  };
}

/** Uma opção do select de status do card. */
export interface OpcaoDeStatus {
  readonly valor: string;
  readonly rotulo: string;
}

/**
 * As opções do select de status do card (`admin.html:6766-6777`): "Pagamento recebido" só aparece
 * quando já é o status (quitação é automática, pelo Financeiro); financeiro vê só o status atual e
 * "Pagamento a receber".
 */
export function opcoesDeStatus(statusAtual: string, papel: Papel): OpcaoDeStatus[] {
  return ORDEM_STATUS.filter((valor) => {
    if (valor === 'pagamento_recebido' && statusAtual !== 'pagamento_recebido') return false;
    if (papel === 'financeiro' && valor !== statusAtual && valor !== 'pagamento_a_receber') return false;
    return true;
  }).map((valor) => ({ valor, rotulo: STATUS_LEAD[valor] ?? valor }));
}

/**
 * A coluna "Pagamento recebido" NÃO aceita soltar pelo MOUSE: o legado nem liga os eventos de
 * arrasto nela (`admin.html:6596`).
 */
export function colunaAceitaSoltarComMouse(status: string): boolean {
  return status !== 'pagamento_recebido';
}

/**
 * COMPORTAMENTO DO LEGADO ISOLADO PARA DECISÃO DO FUNDADOR (achado do inventário, seção 4, e da
 * frente A4, 19/09/2026).
 *
 * O arraste por TOQUE aceita soltar em "Pagamento recebido", e o de MOUSE não: o `pointerup` do
 * legado lê a coluna sob o dedo sem filtro nenhum (`admin.html:6917-6926`), enquanto o mouse nem
 * liga o `drop` nela (`admin.html:6596`). Para o administrador, soltar ali GRAVA o status, porque
 * `moverLeadParaStatus` não barra esse destino.
 *
 * A migração reproduz, idêntico. Se o fundador mandar corrigir, a troca é esta linha: `false`.
 */
export const TOQUE_ACEITA_SOLTAR_EM_PAGAMENTO_RECEBIDO = true;

/** Se o soltar por toque numa coluna chama o movimento. */
export function colunaAceitaSoltarComToque(status: string): boolean {
  return status !== 'pagamento_recebido' || TOQUE_ACEITA_SOLTAR_EM_PAGAMENTO_RECEBIDO;
}

/** O que a tela faz ao pedir um movimento de status, decidido sem banco. */
export type DecisaoDeMovimento =
  // Nada a fazer: lead inexistente ou o mesmo status (`admin.html:6972`).
  | { readonly tipo: 'nada' }
  // Aviso e o quadro volta (`admin.html:6973-6977`, `6983-6992`, `6993-7000`).
  | { readonly tipo: 'avisar'; readonly mensagem: string; readonly titulo?: string; readonly redesenharAntes: boolean }
  // Só redesenhar, sem gravar (`admin.html:6978`).
  | { readonly tipo: 'redesenhar' }
  // Abre o registro do pagamento em vez de gravar (`admin.html:6979-6982`).
  | { readonly tipo: 'registrarPagamento' }
  // Grava o status; `confirmarExclusaoDoPagamento` pede a confirmação antes (`7001-7007`).
  | { readonly tipo: 'mover'; readonly confirmarExclusaoDoPagamento: boolean };

/**
 * O porte de `moverLeadParaStatus` até o ponto de gravar (`admin.html:6970-7007`), na MESMA ordem
 * de perguntas: a ordem decide qual aviso aparece quando mais de uma regra valeria.
 */
export function decidirMovimento(
  lead: Pick<LeadDoQuadro, 'status' | 'nome' | 'produtos'> | undefined,
  novoStatus: string,
  papel: Papel,
): DecisaoDeMovimento {
  if (!lead || lead.status === novoStatus) return { tipo: 'nada' };
  if (papel === 'financeiro' && novoStatus !== 'pagamento_a_receber') {
    return {
      tipo: 'avisar',
      mensagem: 'Seu nível de acesso só permite mover o lead para "Pagamento a receber".',
      redesenharAntes: false,
    };
  }
  if (papel === 'consulta' || papel === 'marketing') return { tipo: 'redesenhar' };
  if (novoStatus === 'pagamento_a_receber') return { tipo: 'registrarPagamento' };
  // Em consultoria e Finalizada só depois de pagamento recebido de verdade (`admin.html:6983-6985`).
  const exigePagamentoRecebido = ['em_consultoria', 'finalizada'];
  const jaTevePagamentoRecebido = ['pagamento_recebido', 'em_consultoria', 'finalizada'].includes(lead.status);
  if (exigePagamentoRecebido.includes(novoStatus) && !jaTevePagamentoRecebido) {
    return {
      tipo: 'avisar',
      mensagem: `"${lead.nome || 'Esse lead'}" ainda não tem pagamento recebido. Quite todas as parcelas na aba Financeiro antes de mover pra "${STATUS_LEAD[novoStatus]}".`,
      titulo: 'Não é possível mover',
      // O legado redesenha ANTES deste aviso, e depois dos outros dois (`admin.html:6988`).
      redesenharAntes: true,
    };
  }
  if (lead.status === 'diagnostico_agendado' && novoStatus === 'proposta_enviada') {
    if (!Array.isArray(lead.produtos) || lead.produtos.length === 0) {
      return { tipo: 'avisar', mensagem: 'Selecione pelo menos um produto antes de enviar a proposta.', redesenharAntes: false };
    }
  }
  // "Para trás" é pela posição na ordem das colunas (`admin.html:7002-7003`).
  const movendoParaTras = ORDEM_STATUS.indexOf(novoStatus) < ORDEM_STATUS.indexOf(lead.status);
  const deveExcluirPagamento = ['pagamento_a_receber', 'pagamento_recebido'].includes(lead.status) && movendoParaTras;
  return { tipo: 'mover', confirmarExclusaoDoPagamento: deveExcluirPagamento };
}

/**
 * O texto do aviso "Jornada não concluída" (`admin.html:7025`). Jornada ausente conta como
 * "Diagnóstico Inicial", e etapa desconhecida também.
 */
export function textoJornadaNaoConcluida(nomeDoLead: string, etapa: string | null | undefined): string {
  const rotulo = (etapa ? JORNADA_ETAPA_LABEL[etapa] : undefined) || 'Diagnóstico Inicial';
  return `"${nomeDoLead || 'Esse lead'}" não foi movido pra Finalizada porque a jornada dela no portal ainda não está em "Concluído" (está em "${rotulo}"). Ajuste a etapa da jornada na ficha da cliente e tente mover de novo.`;
}

/** A jornada que permite Finalizada: etapa ausente conta como "diagnostico_inicial" (`admin.html:7024`). */
export function jornadaConcluida(etapa: string | null | undefined): boolean {
  return (etapa || 'diagnostico_inicial') === 'concluido';
}

/** A frase de confirmação da lixeira (`admin.html:6877`). */
export function textoConfirmarExclusao(nomeDoLead: string): string {
  return `Excluir o lead "${nomeDoLead || ''}"? Não tem como desfazer.`;
}

/** A frase de confirmação de voltar de um status de pagamento (`admin.html:7005`). */
export const TEXTO_CONFIRMAR_VOLTA_DO_PAGAMENTO =
  'Ao voltar para este status o plano de pagamento e as parcelas serão excluídos. Deseja continuar?';
